import { useEffect, useState } from 'react';
import { api } from '../utils/api';

// ─────────────────────────────────────────────────────────────
//  Página pública /completar?token=…
//  La familia que reservó en campamentoaldealix.com (MotoPress) completa
//  aquí las fichas de los campistas y paga el resto de la inscripción
//  (+ autobús opcional). Sin login: el acceso lo da el token del email.
// ─────────────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear();

function splitName(full) {
  const parts = String(full || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName1: '', lastName2: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName1: '', lastName2: '' };
  if (parts.length === 2) return { firstName: parts[0], lastName1: parts[1], lastName2: '' };
  return { firstName: parts[0], lastName1: parts[1], lastName2: parts.slice(2).join(' ') };
}

function emptyCamper(guestName) {
  const { firstName, lastName1, lastName2 } = splitName(guestName);
  return {
    firstName, lastName1, lastName2,
    birthYear: '', sex: '',
    tutorName: '', tutorPhone: '', tutorEmail: '',
    alergias: '', medicacionActual: '', enfermedades: '',
    contactoEmergenciaNombre: '', contactoEmergenciaTelefono: '',
    autorizaCesionDatos: false,
    autorizaImagenRedes: false,
    autorizaMedicacion: false,
  };
}

function getQuery() {
  const p = new URLSearchParams(window.location.search);
  return { token: p.get('token') || '', pago: p.get('pago') || '' };
}

const eur = n => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n || 0);

export default function Completar() {
  const [{ token, pago: pagoParam }] = useState(getQuery);
  const [phase, setPhase] = useState('loading'); // loading | error | done | form | pago | pagado
  const [error, setError] = useState('');
  const [reserva, setReserva] = useState(null);
  const [campers, setCampers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [pagoCampers, setPagoCampers] = useState([]); // [{ name, busTipo }] — elección de bus en el paso de pago

  // Opciones de autobús: vienen del backend (reserva.busOptions) o fallback por defecto.
  const BUS_OPTS = reserva?.busOptions?.length ? reserva.busOptions : [
    { tipo: 'ida_vuelta', label: 'Ida y vuelta', precio: 70 },
  ];
  const precioBus = tipo => (BUS_OPTS.find(o => o.tipo === tipo)?.precio) || 0;

  useEffect(() => {
    if (!token) { setError('Enlace inválido: falta el código de acceso.'); setPhase('error'); return; }
    if (pagoParam === 'ok') { setPhase('pagado'); return; }
    (async () => {
      try {
        const { reserva } = await api.getMotopressReserva(token);
        setReserva(reserva);

        // La reserva está TOTALMENTE terminada solo si el resto está pagado
        // Y el autobús también (o no hay nada de bus que ofrecer).
        const restoPagado = reserva.pagoCompletado || (reserva.remaining || 0) <= 0;
        const todoListo = restoPagado && reserva.busPagado;
        if (todoListo) { setPhase('done'); return; }

        // Si tiene datos (datos ya rellenados): ir al paso de pago a elegir/abonar
        // el autobús y, si procede, el resto. Esto incluye reservas que ya pagaron
        // el resto pero aún no el autobús (busPagado=false).
        const tieneDatos = reserva.estado === 'completed' || reserva.estado === 'ya_registrada' || reserva.datosCompletos;
        if (tieneDatos) {
          // Datos ya rellenados: dejar elegir el autobús en el paso de pago.
          const personas = reserva.personas || [];
          let entries;
          if (personas.length > 0) {
            entries = personas.map(p => ({
              name: p.name || `${p.firstName || ''} ${p.lastName1 || ''}`.trim(),
              busTipo: p.busTipo || (p.bus ? 'ida_vuelta' : 'no'),
            }));
          } else {
            const rooms = Array.isArray(reserva.reservedRooms) ? reserva.reservedRooms : [];
            if (rooms.length > 0) {
              entries = rooms.map(r => ({ name: r.guestName || '', busTipo: 'no' }));
            } else {
              const n = reserva.guests || 1;
              entries = Array.from({ length: n }, () => ({ name: '', busTipo: 'no' }));
            }
          }
          setPagoCampers(entries);
          setPhase('pago');
          return;
        }
        const rooms = Array.isArray(reserva.reservedRooms) ? reserva.reservedRooms : [];
        const n = reserva.guests || rooms.length || 1;
        setCampers(Array.from({ length: n }, (_, i) => emptyCamper(rooms[i]?.guestName)));
        setPhase('form');
        if (pagoParam === 'cancelado') setError('Has cancelado el pago. Puedes intentarlo de nuevo cuando quieras.');
      } catch (e) {
        setError(e.message || 'No se ha podido cargar la reserva. El enlace puede haber caducado.');
        setPhase('error');
      }
    })();
  }, [token, pagoParam]);

  function updateCamper(i, patch) {
    setCampers(prev => prev.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  }

  function updatePagoCamper(i, busTipo) {
    setPagoCampers(prev => prev.map((c, idx) => idx === i ? { ...c, busTipo } : c));
  }

  // Importe del paso de pago, calculado en vivo en cliente.
  // Si el autobús ya está pagado, no se vuelve a cobrar (ni se muestra el selector).
  const busYaPagado = !!reserva?.busPagado;
  const pagoBusTotal = busYaPagado ? 0 : pagoCampers.reduce((s, c) => s + precioBus(c.busTipo), 0);
  const pagoRemaining = reserva?.remaining || 0;
  const pagoAmount = Math.round((pagoRemaining + pagoBusTotal) * 100) / 100;

  // Resumen de importe en vivo dentro del formulario (el autobús se elige en el pago).
  const formRemaining = reserva?.remaining || 0;
  const formTotal = Math.round(formRemaining * 100) / 100;

  async function handleSubmit() {
    setError('');
    for (let i = 0; i < campers.length; i++) {
      const c = campers[i];
      if (!c.firstName.trim()) { setError(`Campista ${i + 1}: el nombre es obligatorio.`); return; }
      if (!c.autorizaCesionDatos) { setError(`Campista ${i + 1}: debes autorizar la cesión de datos para continuar.`); return; }
    }
    const personas = campers.map(c => {
      const birthYear = parseInt(c.birthYear, 10) || null;
      return {
        name: `${c.firstName} ${c.lastName1} ${c.lastName2}`.trim(),
        firstName: c.firstName.trim(), lastName1: c.lastName1.trim(), lastName2: c.lastName2.trim(),
        birthYear, age: birthYear ? CURRENT_YEAR - birthYear : null, sex: c.sex,
        tutorName: c.tutorName.trim(), tutorPhone: c.tutorPhone.trim(), tutorEmail: c.tutorEmail.trim(),
        alergias: c.alergias.trim(), medicacionActual: c.medicacionActual.trim(), enfermedades: c.enfermedades.trim(),
        contactoEmergenciaNombre: c.contactoEmergenciaNombre.trim(), contactoEmergenciaTelefono: c.contactoEmergenciaTelefono.trim(),
        autorizaCesionDatos: !!c.autorizaCesionDatos, autorizaImagenRedes: !!c.autorizaImagenRedes, autorizaMedicacion: !!c.autorizaMedicacion,
      };
    });
    setSubmitting(true);
    try {
      const res = await api.completeMotopressReserva(token, personas);
      window.scrollTo(0, 0);
      // El autobús se elige ahora en el paso de pago, por campista guardado y en orden.
      setPagoCampers(personas.map(p => ({ name: p.name, busTipo: 'no' })));
      setPhase('pago');
    } catch (e) {
      setError(e.message || 'No se han podido guardar los datos. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePay() {
    setError(''); setPaying(true);
    try {
      const { url } = await api.payMotopressReserva(token, pagoCampers.map(c => c.busTipo || 'no'));
      window.location.href = url; // → Stripe Checkout
    } catch (e) {
      setError(e.message || 'No se ha podido iniciar el pago.');
      setPaying(false);
    }
  }

  const fmt = d => d ? new Date(d).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : null;

  return (
    <div className="login-page">
      <div className="login-bg-runes">ᚠᚢᚦᚨᚱᚲᚷᚹᚺᚾᛁᛃᛇᛈᛉᛊᛏᛒᛖᛗᛚᛜᛞᛟ</div>
      <div className="login-card" style={{ maxWidth: 640, width: '100%' }}>
        <div className="login-brand">
          <div className="login-rune">ᚠ</div>
          <h1 className="login-title">Aldealix</h1>
          <p className="login-sub">La Aldea Celta · Inscripción al campamento</p>
        </div>
        <div className="login-divider" />

        {phase === 'loading' && (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}><span className="spinner" /> Cargando tu reserva…</div>
        )}

        {phase === 'error' && <div className="alert alert-error"><span>⚠</span> {error}</div>}

        {phase === 'done' && (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '.5rem' }}>✅</div>
            <h2 style={{ fontSize: '1.1rem', color: 'var(--white)' }}>Todo listo</h2>
            <p style={{ color: 'var(--mid)', marginTop: '.5rem' }}>
              Hemos recibido los datos y el pago de esta reserva. ¡Gracias! Nos vemos en el campamento. 🏕️
            </p>
          </div>
        )}

        {phase === 'pagado' && (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '.5rem' }}>🎉</div>
            <h2 style={{ fontSize: '1.1rem', color: 'var(--white)' }}>¡Pago completado!</h2>
            <p style={{ color: 'var(--mid)', marginTop: '.5rem' }}>
              Tu inscripción está confirmada y el pago se ha realizado correctamente. ¡Nos vemos en el campamento! 🏕️
            </p>
          </div>
        )}

        {phase === 'pago' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '1.2rem' }}>
              <div style={{ fontSize: '2rem' }}>✅</div>
              <h2 style={{ fontSize: '1.1rem', color: 'var(--white)', margin: '.3rem 0' }}>Datos guardados</h2>
              <p style={{ color: 'var(--mid)', fontSize: '.88rem' }}>Elige el transporte y abona el resto de la inscripción.</p>
            </div>

            {busYaPagado ? (
              <div className="member-box" style={{ marginBottom: '1rem' }}>
                <div className="section-label">🚌 Autobús ya pagado ✅</div>
                <p style={{ color: 'var(--mid)', fontSize: '.82rem' }}>
                  El transporte de esta reserva ya está abonado. No hay nada más que elegir.
                </p>
              </div>
            ) : (
            <div className="member-box" style={{ marginBottom: '1rem' }}>
              <div className="section-label">🚌 Autobús (opcional)</div>
              <p style={{ color: 'var(--mid)', fontSize: '.82rem', marginBottom: '.8rem' }}>
                Desde <strong style={{ color: 'var(--white)' }}>Madrid (Moncloa)</strong>. Elige el transporte para cada campista que lo necesite.
              </p>
              {pagoCampers.map((c, i) => {
                const activo = c.busTipo && c.busTipo !== 'no';
                return (
                  <div key={i} style={{
                    padding: '.7rem .9rem', marginBottom: '.5rem', borderRadius: 10,
                    border: `1px solid ${activo ? 'var(--fern)' : 'var(--green-border)'}`,
                    background: activo ? 'var(--green-pale)' : 'rgba(255,255,255,.02)',
                    transition: 'all .15s',
                  }}>
                    <div style={{ color: 'var(--white)', fontWeight: 600, marginBottom: '.5rem' }}>
                      {c.name || `Campista ${i + 1}`}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem' }}>
                      <button
                        type="button"
                        className={`btn btn-sm ${!activo ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => updatePagoCamper(i, 'no')}
                      >
                        Sin autobús
                      </button>
                      {BUS_OPTS.map(o => (
                        <button
                          key={o.tipo}
                          type="button"
                          className={`btn btn-sm ${c.busTipo === o.tipo ? 'btn-primary' : 'btn-ghost'}`}
                          onClick={() => updatePagoCamper(i, o.tipo)}
                        >
                          {o.label} · {eur(o.precio)}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            )}

            <div className="member-box" style={{ marginBottom: '1rem' }}>
              <div className="section-label">Resumen del pago</div>
              {pagoRemaining > 0
                ? <Row label="Resto de la inscripción" value={eur(pagoRemaining)} />
                : <Row label={<span style={{ color: 'var(--mid)', opacity: .7 }}>Resto de la inscripción</span>} value={<span style={{ color: 'var(--mid)', opacity: .7 }}>pagado ✅</span>} />}
              {!busYaPagado && BUS_OPTS.map(o => {
                const cantidad = pagoCampers.filter(c => c.busTipo === o.tipo).length;
                if (cantidad <= 0) return null;
                return (
                  <Row
                    key={o.tipo}
                    label={`Autobús ${o.label.toLowerCase()} — ${cantidad} × ${eur(o.precio)}`}
                    value={eur(cantidad * o.precio)}
                  />
                );
              })}
              <div style={{ borderTop: '1px solid var(--green-border)', marginTop: '.5rem', paddingTop: '.5rem' }}>
                <Row label={<strong style={{ color: 'var(--white)' }}>Total a pagar</strong>} value={<strong style={{ color: 'var(--amber)', fontSize: '1.1rem' }}>{eur(pagoAmount)}</strong>} />
              </div>
            </div>
            {error && <div className="alert alert-error" style={{ marginBottom: '.5rem' }}><span>⚠</span> {error}</div>}
            <button className="btn btn-primary btn-full btn-lg" onClick={handlePay} disabled={paying || pagoAmount <= 0}>
              {paying
                ? <><span className="spinner" /> Redirigiendo al pago…</>
                : (pagoAmount > 0 ? `💳 Pagar ${eur(pagoAmount)} con tarjeta` : 'No hay nada pendiente')}
            </button>
            <p style={{ color: 'var(--mid)', fontSize: '.78rem', textAlign: 'center', marginTop: '.6rem' }}>Pago seguro con Stripe. Serás redirigido a una página segura.</p>
          </>
        )}

        {phase === 'form' && reserva && (
          <>
            <div className="member-box" style={{ marginBottom: '1.2rem' }}>
              <div className="section-label">Tu reserva</div>
              <div style={{ fontSize: '.9rem', color: 'var(--white)', lineHeight: 1.7 }}>
                <div>Reserva <strong>#{reserva.externalId}</strong></div>
                {reserva.checkInDate && <div>Fechas: {fmt(reserva.checkInDate)} → {fmt(reserva.checkOutDate)}</div>}
                {reserva.matchedEvents?.length > 0 && (
                  <div>Semanas: {reserva.matchedEvents.map(e => (/Semana\s*\d+/i.exec(e.name) || [e.name])[0]).join(', ')}</div>
                )}
                <div>Plazas: {reserva.guests}</div>
                {reserva.remaining > 0 && <div>Resto pendiente de pago: <strong style={{ color: 'var(--amber)' }}>{eur(reserva.remaining)}</strong></div>}
              </div>
            </div>

            <p style={{ color: 'var(--mid)', fontSize: '.88rem', marginBottom: '1rem' }}>
              Completa la ficha de {campers.length > 1 ? `los ${campers.length} campistas` : 'el campista'}.
              Los campos con <span className="req">*</span> son obligatorios.
            </p>

            {campers.map((c, i) => (
              <div className="member-box" key={i} style={{ marginBottom: '1rem' }}>
                <div className="member-label" style={{ marginBottom: '.7rem' }}>🧒 Campista {i + 1}</div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.7rem' }}>
                  <Field label="Nombre" req value={c.firstName} onChange={v => updateCamper(i, { firstName: v })} />
                  <Field label="Primer apellido" value={c.lastName1} onChange={v => updateCamper(i, { lastName1: v })} />
                  <Field label="Segundo apellido" value={c.lastName2} onChange={v => updateCamper(i, { lastName2: v })} />
                  <Field label="Año de nacimiento" value={c.birthYear} onChange={v => updateCamper(i, { birthYear: v.replace(/\D/g, '').slice(0, 4) })} placeholder="Ej. 2014" />
                  <div className="form-group">
                    <label className="form-label">Sexo</label>
                    <select className="form-input" value={c.sex} onChange={e => updateCamper(i, { sex: e.target.value })}>
                      <option value="">—</option><option value="F">Niña</option><option value="M">Niño</option><option value="X">Otro</option>
                    </select>
                  </div>
                </div>

                <div className="section-label" style={{ marginTop: '1rem' }}>Tutor / madre / padre</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.7rem' }}>
                  <Field label="Nombre del tutor" value={c.tutorName} onChange={v => updateCamper(i, { tutorName: v })} />
                  <Field label="Teléfono del tutor" value={c.tutorPhone} onChange={v => updateCamper(i, { tutorPhone: v })} />
                  <Field label="Email del tutor" value={c.tutorEmail} onChange={v => updateCamper(i, { tutorEmail: v })} />
                </div>

                <div className="section-label" style={{ marginTop: '1rem' }}>Salud</div>
                <Field label="Alergias" value={c.alergias} onChange={v => updateCamper(i, { alergias: v })} textarea placeholder="Indica alergias o «ninguna»" />
                <Field label="Medicación actual" value={c.medicacionActual} onChange={v => updateCamper(i, { medicacionActual: v })} textarea />
                <Field label="Enfermedades / condiciones" value={c.enfermedades} onChange={v => updateCamper(i, { enfermedades: v })} textarea />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.7rem' }}>
                  <Field label="Contacto de emergencia" value={c.contactoEmergenciaNombre} onChange={v => updateCamper(i, { contactoEmergenciaNombre: v })} />
                  <Field label="Teléfono de emergencia" value={c.contactoEmergenciaTelefono} onChange={v => updateCamper(i, { contactoEmergenciaTelefono: v })} />
                </div>

                <div className="section-label" style={{ marginTop: '1rem' }}>Autorizaciones</div>
                <Toggle checked={c.autorizaCesionDatos} onChange={e => updateCamper(i, { autorizaCesionDatos: e.target.checked })}>
                  Autorizo la cesión de datos personales para la gestión del campamento. <span className="req">*</span>
                </Toggle>
                <Toggle checked={c.autorizaImagenRedes} onChange={e => updateCamper(i, { autorizaImagenRedes: e.target.checked })}>
                  Autorizo el uso de imágenes en redes y materiales del campamento.
                </Toggle>
                <Toggle checked={c.autorizaMedicacion} onChange={e => updateCamper(i, { autorizaMedicacion: e.target.checked })}>
                  Autorizo la administración de medicación según indicaciones.
                </Toggle>
              </div>
            ))}

            {formTotal > 0 && (
              <div className="member-box" style={{ marginBottom: '1rem' }}>
                <div className="section-label">Importe a pagar tras guardar</div>
                <Row label="Resto de la inscripción" value={eur(formRemaining)} />
                <div style={{ borderTop: '1px solid var(--green-border)', marginTop: '.5rem', paddingTop: '.5rem' }}>
                  <Row label={<strong style={{ color: 'var(--white)' }}>Total</strong>} value={<strong style={{ color: 'var(--amber)' }}>{eur(formTotal)}</strong>} />
                </div>
                <p style={{ color: 'var(--mid)', fontSize: '.78rem', marginTop: '.5rem' }}>El autobús opcional se elige en el siguiente paso.</p>
              </div>
            )}

            {error && <div className="alert alert-error" style={{ marginTop: '.5rem' }}><span>⚠</span> {error}</div>}

            <button className="btn btn-primary btn-full btn-lg" style={{ marginTop: '1rem' }} onClick={handleSubmit} disabled={submitting}>
              {submitting ? <><span className="spinner" /> Guardando…</> : (formTotal > 0 ? 'Guardar y continuar al pago →' : 'Guardar inscripción')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Interruptor con la estructura que espera el CSS del tema (.toggle-wrap oculta
// el checkbox nativo y muestra .toggle-track / .toggle-thumb).
function Toggle({ checked, onChange, children, style }) {
  return (
    <label className="toggle-wrap" style={{ alignItems: 'flex-start', marginBottom: '.5rem', ...style }}>
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="toggle-track" style={{ flex: '0 0 auto', marginTop: '2px' }}><span className="toggle-thumb" /></span>
      <span>{children}</span>
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '.9rem', color: 'var(--mid)', padding: '.15rem 0' }}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

function Field({ label, value, onChange, req, placeholder, textarea }) {
  return (
    <div className="form-group">
      <label className="form-label">{label} {req && <span className="req">*</span>}</label>
      {textarea
        ? <textarea className="form-input" rows={2} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
        : <input className="form-input" value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />}
    </div>
  );
}
